import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { colors, shadows } from '../styles/GlobalStyles';
import { rv, containerWidth, mediaQueries } from '../styles/responsive';
import { Section, SectionLabel, Button } from './ui';
import { MailIcon, PhoneIcon, SendIcon } from './ui/Icons';

const ContactSection = styled(Section)`
  background-color: ${colors.slate[50]};
  padding: ${rv(80, 48)} 0;
  justify-content: center;
  align-items: center;
`;

const ContactContainer = styled.div`
  width: 100%;
  max-width: ${containerWidth(1152, 24)};
  margin: 0 auto;
  padding: 0 ${rv(24, 20)};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rv(80, 40)};
  z-index: 10;

  ${mediaQueries.tabletUp} {
    flex-direction: row;
    justify-content: space-between;
  }
`;

const InfoSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  margin-top: ${rv(0, 32)};

  ${mediaQueries.tabletUp} {
    width: 50%;
    margin-top: 0;
  }
`;

const Title = styled.h2`
  font-size: ${rv(48, 28)};
  font-weight: 800;
  color: ${colors.slate[900]};
  margin-bottom: ${rv(24, 20)};
  letter-spacing: -0.025em;
  line-height: 1.2;
`;

const Description = styled.p`
  font-size: ${rv(18, 14)};
  color: ${colors.slate[500]};
  margin-bottom: ${rv(40, 32)};
  line-height: 1.75;
  max-width: 448px;
`;

const ContactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rv(24, 20)};
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${rv(20, 16)};
`;

const ContactIconBox = styled.div`
  width: ${rv(56, 48)};
  height: ${rv(56, 48)};
  border-radius: ${rv(16, 12)};
  background: ${colors.white};
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  border: 1px solid ${colors.slate[100]};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.slate[700]};

  svg {
    width: ${rv(24, 20)};
    height: ${rv(24, 20)};
  }
`;

const ContactInfo = styled.div``;

const ContactLabel = styled.p`
  font-size: ${rv(14, 12)};
  color: ${colors.slate[500]};
  font-weight: 500;
  margin-bottom: 4px;
`;

const ContactValue = styled.p`
  font-size: ${rv(18, 14)};
  font-weight: 700;
  color: ${colors.slate[900]};
`;

const FormSection = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;

  ${mediaQueries.tabletUp} {
    width: 50%;
    justify-content: flex-end;
  }
`;

const FormCard = styled.div`
  background: ${colors.white};
  padding: ${rv(40, 24)};
  border-radius: ${rv(40, 32)};
  box-shadow: ${shadows.bento};
  width: 100%;
  max-width: 512px;
  border: 1px solid ${colors.slate[100]};
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: ${shadows.bentoHover};
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${rv(24, 20)};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rv(8, 6)};
`;

const Label = styled.label`
  font-size: ${rv(14, 12)};
  font-weight: 600;
  color: ${colors.slate[700]};
  margin-left: 4px;
`;

const Input = styled.input`
  width: 100%;
  background: ${colors.slate[50]};
  border: 1px solid ${colors.slate[100]};
  border-radius: ${rv(16, 12)};
  padding: ${rv(16, 14)} ${rv(20, 16)};
  font-size: ${rv(14, 13)};
  color: ${colors.slate[900]};
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);

  &::placeholder {
    color: ${colors.slate[400]};
  }

  &:focus {
    outline: none;
    background: ${colors.white};
    border-color: ${colors.brand[900]};
    box-shadow: 0 0 0 3px ${colors.brand[100]};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  background: ${colors.slate[50]};
  border: 1px solid ${colors.slate[100]};
  border-radius: ${rv(16, 12)};
  padding: ${rv(16, 14)} ${rv(20, 16)};
  font-size: ${rv(14, 13)};
  color: ${colors.slate[900]};
  transition: all 0.2s ease;
  resize: none;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);

  &::placeholder {
    color: ${colors.slate[400]};
  }

  &:focus {
    outline: none;
    background: ${colors.white};
    border-color: ${colors.brand[900]};
    box-shadow: 0 0 0 3px ${colors.brand[100]};
  }
`;

const SubmitButton = styled(Button)`
  width: 100%;
  margin-top: 8px;
  padding: ${rv(16, 14)} ${rv(32, 24)};
  border-radius: ${rv(16, 12)};
  font-size: ${rv(14, 13)};
  gap: 8px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
    transform: none;
  }
`;

const HoneypotField = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
`;

const StatusMessage = styled.p<{ $error: boolean }>`
  min-height: 20px;
  margin: -8px 4px 0;
  color: ${({ $error }) => ($error ? '#dc2626' : '#15803d')};
  font-size: ${rv(14, 12)};
  line-height: 1.5;
`;

const defaultApiBase = process.env.NODE_ENV === 'production' ? '/neural/api' : '/api';
const contactApiBase = (process.env.REACT_APP_API_BASE_URL || defaultApiBase).replace(/\/$/, '');

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    website: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionState, setSubmissionState] = useState<'idle' | 'success' | 'error'>('idle');
  const abortControllerRef = useRef<AbortController | null>(null);
  const submittingRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      abortControllerRef.current?.abort();
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (submissionState !== 'idle') {
      setSubmissionState('idle');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;

    submittingRef.current = true;
    setIsSubmitting(true);
    setSubmissionState('idle');
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const timeout = window.setTimeout(() => controller.abort(), 15000);

    try {
      const response = await fetch(`${contactApiBase}/contact/`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
        signal: controller.signal,
      });
      const payload = await response.json().catch(() => null);
      if (
        !response.ok ||
        !payload ||
        typeof payload !== 'object' ||
        (payload as { status?: unknown }).status !== 'sent'
      ) {
        throw new Error('contact_delivery_failed');
      }
      if (mountedRef.current) {
        setFormData({ name: '', email: '', message: '', website: '' });
        setSubmissionState('success');
      }
    } catch {
      if (mountedRef.current) {
        setSubmissionState('error');
      }
    } finally {
      window.clearTimeout(timeout);
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
      submittingRef.current = false;
      if (mountedRef.current) {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <ContactSection id="contact">
      <ContactContainer>
        <InfoSection>
          <SectionLabel>Contact Us</SectionLabel>
          <Title>
            비즈니스의 혁신,<br />
            지금 바로 논의하세요.
          </Title>
          <Description>
            AI 모델 도입부터 맞춤형 시스템 구축까지, 뉴럴네트워크의 최고 기술 전문가 팀이 가장 완벽한 해답을 제시해 드립니다.
          </Description>

          <ContactList>
            <ContactItem>
              <ContactIconBox>
                <MailIcon />
              </ContactIconBox>
              <ContactInfo>
                <ContactLabel>이메일 문의</ContactLabel>
                <ContactValue>support@neuralnet-space.com</ContactValue>
              </ContactInfo>
            </ContactItem>
            <ContactItem>
              <ContactIconBox>
                <PhoneIcon />
              </ContactIconBox>
              <ContactInfo>
                <ContactLabel>고객 센터</ContactLabel>
                <ContactValue>010-8240-4422</ContactValue>
              </ContactInfo>
            </ContactItem>
          </ContactList>
        </InfoSection>

        <FormSection>
          <FormCard>
            <Form onSubmit={handleSubmit} aria-describedby="contact-form-status">
              <HoneypotField aria-hidden="true">
                <Label htmlFor="contact-website">웹사이트</Label>
                <Input
                  id="contact-website"
                  type="text"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </HoneypotField>
              <FormGroup>
                <Label htmlFor="contact-name">이름 / 회사명</Label>
                <Input
                  id="contact-name"
                  type="text"
                  name="name"
                  placeholder="홍길동 (주식회사 넥서스)"
                  value={formData.name}
                  onChange={handleChange}
                  maxLength={120}
                  autoComplete="name"
                  disabled={isSubmitting}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="contact-email">이메일 주소</Label>
                <Input
                  id="contact-email"
                  type="email"
                  name="email"
                  placeholder="hello@company.com"
                  value={formData.email}
                  onChange={handleChange}
                  maxLength={254}
                  autoComplete="email"
                  disabled={isSubmitting}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="contact-message">프로젝트 문의 내용</Label>
                <Textarea
                  id="contact-message"
                  name="message"
                  rows={4}
                  placeholder="AI 도입 목적이나 기존 시스템의 문제점 등 필요한 솔루션에 대해 자유롭게 적어주세요."
                  value={formData.message}
                  onChange={handleChange}
                  maxLength={5000}
                  disabled={isSubmitting}
                  required
                />
              </FormGroup>
              <StatusMessage
                id="contact-form-status"
                role={submissionState === 'error' ? 'alert' : 'status'}
                aria-live="polite"
                $error={submissionState === 'error'}
              >
                {submissionState === 'success' && '문의가 정상적으로 접수되었습니다.'}
                {submissionState === 'error' && '문의 전송에 실패했습니다. 잠시 후 다시 시도해 주세요.'}
              </StatusMessage>
              <SubmitButton type="submit" disabled={isSubmitting} aria-busy={isSubmitting}>
                {isSubmitting ? '전송 중...' : '문의 접수하기'}
                <SendIcon />
              </SubmitButton>
            </Form>
          </FormCard>
        </FormSection>
      </ContactContainer>
    </ContactSection>
  );
};

export default Contact;
